import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { CreateOrganizationModal } from '../components/organizations/CreateOrganizationModal';
import { mockOrganizations } from '../utils/mockData';
import { Plus, Search, ArrowUpDown } from 'lucide-react';
import { useToast } from '../hooks/useToast';
export function Organizations() {
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'name' | 'storeCount' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const handleCreateOrganization = (data: any) => {
    showToast(`Organization "${data.name}" created successfully`, 'success');
    // In a real app, this would POST to API
  };
  const handleRowClick = (orgId: string) => {
    navigate(`/organizations/${orgId}`);
  };
  const handleSort = (field: 'name' | 'storeCount') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Filter and sort organizations
  let filteredOrgs = mockOrganizations.filter(org => org.name.toLowerCase().includes(searchQuery.toLowerCase()) || org.contactName.toLowerCase().includes(searchQuery.toLowerCase()) || org.contactEmail.toLowerCase().includes(searchQuery.toLowerCase()));
  if (sortField) {
    filteredOrgs = [...filteredOrgs].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const modifier = sortDirection === 'asc' ? 1 : -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * modifier;
      }
      return ((aVal as number) - (bVal as number)) * modifier;
    });
  }
  return <div className="space-y-6" data-id="element-986">
      <div className="flex items-center justify-between" data-id="element-987">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-988">Organizations</h1>
        <Button onClick={() => setIsCreateModalOpen(true)} data-id="element-989">
          <Plus className="h-4 w-4 mr-2" data-id="element-990" />
          New Organization
        </Button>
      </div>

      <Card data-id="element-991">
        <div className="mb-4" data-id="element-992">
          <div className="relative" data-id="element-993">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-994" />
            <input type="text" placeholder="Search organizations..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500" data-id="element-995" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-996">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-997">
            <thead className="bg-gray-50" data-id="element-998">
              <tr data-id="element-999">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')} data-id="element-1000">
                  <div className="flex items-center space-x-1" data-id="element-1001">
                    <span data-id="element-1002">Name</span>
                    <ArrowUpDown className="h-4 w-4" data-id="element-1003" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1004">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1005">
                  Timezone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100" onClick={() => handleSort('storeCount')} data-id="element-1006">
                  <div className="flex items-center space-x-1" data-id="element-1007">
                    <span data-id="element-1008">Active Stores</span>
                    <ArrowUpDown className="h-4 w-4" data-id="element-1009" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1010">
                  Location
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1011">
              {filteredOrgs.map(org => <tr key={org.id} onClick={() => handleRowClick(org.id)} className="hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-1012">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-1013">
                    {org.name}
                  </td>
                  <td className="px-6 py-4" data-id="element-1014">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1015">
                      {org.contactName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-1016">
                      {org.contactEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1017">
                    {org.defaultTimezone}
                  </td>
                  <td className="px-6 py-4" data-id="element-1018">
                    <button onClick={e => {
                  e.stopPropagation();
                  navigate(`/organizations/${org.id}?tab=stores`);
                }} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-1019">
                      {org.storeCount}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1020">
                    {org.defaultDeliveryAddress}
                  </td>
                </tr>)}
            </tbody>
          </table>

          {filteredOrgs.length === 0 && <div className="text-center py-12 bg-gray-50" data-id="element-1021">
              <p className="text-gray-500" data-id="element-1022">
                No organizations found matching your search.
              </p>
            </div>}
        </div>
      </Card>

      <CreateOrganizationModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSave={handleCreateOrganization} data-id="element-1023" />
    </div>;
}